import { ApiPrintAttachmentOption, EnumPrintAttachmentKind, type ApiPrintOption } from '@Model';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useIsPortalSession } from '@Contexts/session';
import { Card } from '@synqo/frontend-component-library';
import { DocumentPreview } from './DocumentPreview';
import { RawAttachmentDownloadButton } from './RawAttachmentDownloadButton';

interface IMultiDocumentPreviewProperties {
    printOption: ApiPrintOption;
    primaryId: string;
    onError?: () => void;
}

export function MultiDocumentPreview({ printOption, primaryId, onError: onError }: IMultiDocumentPreviewProperties) {
    const isPortalSession = useIsPortalSession();
    const hasPermission = useHasPermission();
    const showAttachments = !isPortalSession && hasPermission(Permission.DocumentsDocumentCreate);

    if (printOption.attachments.length === 0 || !showAttachments) {
        return (
            <DocumentPreview
                printOptionId={printOption.id}
                primaryId={primaryId}
                previewFileId={printOption.existing_preview_id}
                onError={onError}
            />
        );
    }

    const printouts = printOption.attachments.filter((a) => a.kind !== EnumPrintAttachmentKind.Raw);
    const rawAttachments = printOption.attachments.filter((a) => a.kind === EnumPrintAttachmentKind.Raw);

    return (
        <>
            <Card
                label={printOption.display_string}
                collapsable={false}
                name="mainDocument"
                padding="sm"
                border="withoutBorder"
            >
                <DocumentPreview
                    printOptionId={printOption.id}
                    primaryId={primaryId}
                    previewFileId={printOption.existing_preview_id}
                    onError={onError}
                />
            </Card>
            {printouts.map((a) => (
                <PrintoutAttachmentCard
                    key={`${a.id}-${a.print_payload.primary_id}`}
                    attachment={a}
                    onError={onError}
                />
            ))}
            {rawAttachments.length === 0 ? undefined : (
                <Card name="attachments" label="Anhänge" padding="sm" closed>
                    {rawAttachments.map((a) => (
                        <RawAttachmentDownloadButton key={`${a.id}-${a.attachment_id}`} attachment={a} />
                    ))}
                </Card>
            )}
        </>
    );
}

interface AttachmentCardProps {
    attachment: ApiPrintAttachmentOption;
    onError?: (() => void) | undefined;
}

function PrintoutAttachmentCard({ attachment, onError }: AttachmentCardProps) {
    return (
        <Card name={attachment.id} label={attachment.display_name} padding="sm">
            <DocumentPreview
                printOptionId={attachment.id}
                primaryId={attachment.print_payload.primary_id}
                onError={onError}
            />
        </Card>
    );
}
